import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FiCompass, FiHeart, FiSearch } from 'react-icons/fi';
import { FaUserCircle } from 'react-icons/fa';

import { useAuth } from '../../hooks/auth';
import { useFavorites } from '../../hooks/favorites';
import { useFilters } from '../../hooks/projectFilters';
import LogoSvg from '../../assets/svg/logo-blue.svg';
import Button from '../Button';
import BurgerMenu from '../BurgerMenu';
import ThemeToggle from '../ThemeToggle';

import {
  Actions,
  Badge,
  Container,
  Header,
  IconButton,
  Link,
  LogoContainer,
  MobileSearch,
  Nav,
  NavButton,
  SearchInput,
  InputContainer,
  UserImage,
  UserLink,
} from './styles';

const HeaderComponent = () => {
  const { setSearchString, searchString } = useFilters();
  const history = useHistory();
  const location = useLocation();
  const { resetFilters } = useFilters();
  const { isLoggedIn, userImage, userType, userName } = useAuth();
  const { favoritesCount } = useFavorites();

  const [searchValue, setSearchValue] = useState('');
  const [windowWidth, setWindowSize] = useState(0);

  useEffect(() => {
    function handleResize() {
      setWindowSize(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSearchValue(searchString);
  }, [searchString]);

  function handleLoginButtonClick() {
    history.push('/login');
  }

  function handleExploreButtonClick() {
    if (location.pathname !== '/projects') {
      history.push('/projects');
      resetFilters();
    }
  }

  function handleLogoClick() {
    history.push('/');
  }

  function handleFavoritesClick() {
    history.push('/favorites');
  }

  function handleInputKeyUp(e) {
    if (e.key === 'Enter') {
      setSearchString(e.target.value);
      if (location.pathname !== '/projects') {
        history.push(`/projects`);
      }
    }
  }

  function handleInputChange(e) {
    setSearchValue(e.target.value);
  }

  function renderSearchInput() {
    return (
      <InputContainer>
        <FiSearch size={18} aria-hidden />
        <SearchInput
          type="text"
          placeholder="O que você está procurando?"
          aria-label="Buscar projetos"
          onKeyUp={handleInputKeyUp}
          onChange={handleInputChange}
          value={searchValue}
        />
      </InputContainer>
    );
  }

  function renderFullHeader() {
    return (
      <Header>
        <LogoContainer src={LogoSvg} alt="Portal Due It" onClick={handleLogoClick} />

        <Nav>
          <NavButton
            type="button"
            onClick={handleExploreButtonClick}
            active={location.pathname === '/projects' ? 1 : 0}
          >
            <FiCompass size={17} />
            Explorar
          </NavButton>
        </Nav>

        {renderSearchInput()}

        <Actions>
          {/* Controles de usuário só existem com sessão ativa. */}
          {isLoggedIn && (
            <IconButton
              type="button"
              onClick={handleFavoritesClick}
              aria-label={
                favoritesCount
                  ? `Favoritos (${favoritesCount})`
                  : 'Favoritos'
              }
              title="Favoritos"
            >
              <FiHeart size={19} />
              {favoritesCount > 0 && (
                <Badge>{favoritesCount > 9 ? '9+' : favoritesCount}</Badge>
              )}
            </IconButton>
          )}

          <ThemeToggle />

          {isLoggedIn ? (
            <UserLink to={`/${userType}/profile`}>
              {userImage ? (
                <UserImage src={userImage} alt="Imagem do usuário" />
              ) : (
                <FaUserCircle size={30} />
              )}
              <span>
                {`${userName}${userType === 'designer' ? ' · Designer' : ''}`}
              </span>
            </UserLink>
          ) : (
            <>
              <Link to="/register">Cadastre-se</Link>
              <Button text="Entrar" primary onClick={handleLoginButtonClick} />
            </>
          )}
        </Actions>
      </Header>
    );
  }

  function renderMinifiedHeader() {
    return (
      <>
        <Header>
          <LogoContainer
            open
            src={LogoSvg}
            alt="Portal Due It"
            onClick={handleLogoClick}
          />
          <Actions>
            {isLoggedIn && (
              <IconButton
                type="button"
                onClick={handleFavoritesClick}
                aria-label="Favoritos"
              >
                <FiHeart size={19} />
                {favoritesCount > 0 && (
                  <Badge>{favoritesCount > 9 ? '9+' : favoritesCount}</Badge>
                )}
              </IconButton>
            )}
            <ThemeToggle />
            <BurgerMenu />
          </Actions>
        </Header>
        <MobileSearch>{renderSearchInput()}</MobileSearch>
      </>
    );
  }

  return (
    <Container>
      {windowWidth > 980 ? renderFullHeader() : renderMinifiedHeader()}
    </Container>
  );
};

export default HeaderComponent;
